import { GridLegacy as Grid, styled } from '@mui/material'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import SensitivityMatrixItem from './SensitivityMatrixItem'
import { decimalToLocalizedString, Percentage, tokens } from 'ComponentLibrary'
import { SensitivityMatrixType, SensitivityMatrixValues } from 'ComponentLibrary/src/common/sensitivity-matrix'
import type { SensitivityMatrixCopy } from './SensitivityMatrix'

export type SensitivityMatrixBodyProps = {
    sensitivityMatrix?: SensitivityMatrixType
    copy?: SensitivityMatrixCopy
    locale?: string
}

type SensitivityMatrixValuesWithDeltaOrder = Omit<SensitivityMatrixValues, 'delta_to_base'> & {
    delta_to_base: { delta?: number; order?: number }
}

type MatrixRowProps = {
    decreased?: SensitivityMatrixValuesWithDeltaOrder
    base?: SensitivityMatrixValuesWithDeltaOrder
    increased?: SensitivityMatrixValuesWithDeltaOrder
    locale: string
}

const getMatrixItemProps = (data?: SensitivityMatrixValuesWithDeltaOrder) => ({
    assetValue: data?.asset_value,
    deltaToBase: data?.delta_to_base,
    assetValuePerSqm: data?.asset_value_per_sqm,
    multiplier: data?.multiplier,
    yieldRate: data?.yield_rate,
})

function getMatrixValuesWithDeltaOrder(matrixValues: (SensitivityMatrixValues | undefined)[]) {
    const sortedDeltas = matrixValues
        .map((val) => val?.delta_to_base)
        .sort((a, b) => {
            if (a === undefined) {
                return -1
            }
            if (b === undefined) {
                return 1
            }
            return a - b
        })
    const matrixValuesWithDeltaOrder = new Map<number | undefined, number>()

    sortedDeltas.forEach((delta, index) => {
        matrixValuesWithDeltaOrder.set(delta, delta === 0 ? 4 : 8 - index)
    })

    return matrixValues.map((val?: SensitivityMatrixValues) => {
        return {
            ...val,
            delta_to_base: { delta: val?.delta_to_base, order: matrixValuesWithDeltaOrder.get(val?.delta_to_base) },
        }
    })
}

const MatrixRow = ({ decreased, base, increased, locale }: MatrixRowProps) => (
    <>
        <SensitivityMatrixItem {...getMatrixItemProps(decreased)} locale={locale} />
        <SensitivityMatrixItem {...getMatrixItemProps(base)} locale={locale} />
        <SensitivityMatrixItem {...getMatrixItemProps(increased)} locale={locale} />
    </>
)

const formatDiscountRateChange = (change: number | undefined, percentagePointUnit: string, locale: string) => {
    if (change === undefined) {
        return 'N/A'
    }

    const isBasisPointUnit = percentagePointUnit === 'BP'
    const value = isBasisPointUnit ? change * 10000 : change * 100
    const numberOfDigits = isBasisPointUnit ? 0 : 2

    return decimalToLocalizedString(value, false, numberOfDigits, numberOfDigits, locale)
}

export default function SensitivityMatrixBody({ sensitivityMatrix, copy, locale = 'de-DE' }: SensitivityMatrixBodyProps) {
    const results = sensitivityMatrix?.results
    const percentagePointUnit = copy?.percentagePointUnit ?? 'BP'

    const matrixArray = [
        results?.decreased_rent?.decreased_discount_rate,
        results?.decreased_rent?.base_discount_rate,
        results?.decreased_rent?.increased_discount_rate,
        results?.base_rent?.decreased_discount_rate,
        results?.base_rent?.base_discount_rate,
        results?.base_rent?.increased_discount_rate,
        results?.increased_rent?.decreased_discount_rate,
        results?.increased_rent?.base_discount_rate,
        results?.increased_rent?.increased_discount_rate,
    ]

    const matrixValuesWithOrderedDeltas = getMatrixValuesWithDeltaOrder(matrixArray)

    const decreasedRent = matrixValuesWithOrderedDeltas.slice(0, 3)
    const baseRent = matrixValuesWithOrderedDeltas.slice(3, 6)
    const increasedRent = matrixValuesWithOrderedDeltas.slice(6, 9)

    return (
        <TableContainer>
            <ColumnLabels>
                <Grid></Grid>
                <Grid display="flex" direction="row">
                    <Grid width="2%">
                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                            {copy?.marketRentLabel ?? 'MIETEN'}
                        </Typography>
                    </Grid>
                    <Grid width="calc(100% - 20px)">
                        {/* <Grid width="98%"> */}
                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                            -{' '}
                            {sensitivityMatrix?.rent_change !== undefined
                                ? new Percentage(sensitivityMatrix.rent_change).format({ numberOfDigits: 0, withSign: false, locale })
                                : 'N/A'}
                        </Typography>
                    </Grid>
                </Grid>
                <Typography variant="textS" color={tokens.ColorTextDefault}>
                    +/- 0 %
                </Typography>
                <Typography variant="textS" color={tokens.ColorTextDefault}>
                    +{' '}
                    {sensitivityMatrix?.rent_change !== undefined
                        ? new Percentage(sensitivityMatrix.rent_change).format({ numberOfDigits: 0, withSign: false, locale })
                        : 'N/A'}
                </Typography>
            </ColumnLabels>
            <RowLabels>
                {/* <Grid position="absolute" alignSelf="flex-start" width="56px"> */}
                <Grid>
                    <Typography variant="textS" color={tokens.ColorTextDefault}>
                        {copy?.discountRateLabel ?? 'LZ'}
                    </Typography>
                </Grid>
                <Typography variant="textS" color={tokens.ColorTextDefault}>
                    - {formatDiscountRateChange(sensitivityMatrix?.discount_rate_change, percentagePointUnit, locale)} {percentagePointUnit}
                </Typography>
                <Typography variant="textS" color={tokens.ColorTextDefault}>
                    +/- 0 {percentagePointUnit}
                </Typography>
                <Typography variant="textS" color={tokens.ColorTextDefault}>
                    + {formatDiscountRateChange(sensitivityMatrix?.discount_rate_change, percentagePointUnit, locale)} {percentagePointUnit}
                </Typography>
            </RowLabels>
            <MatrixItemsContainer>
                <MatrixRow decreased={decreasedRent[0]} base={baseRent[0]} increased={increasedRent[0]} locale={locale} />
                <MatrixRow decreased={decreasedRent[1]} base={baseRent[1]} increased={increasedRent[1]} locale={locale} />
                <MatrixRow decreased={decreasedRent[2]} base={baseRent[2]} increased={increasedRent[2]} locale={locale} />
            </MatrixItemsContainer>
        </TableContainer>
    )
}

const TableContainer = styled('div')`
    display: grid;
    gap: ${tokens.SizingSpacing150};
    grid-template-areas:
        'column-label column-label column-label column-label'
        'row-label main main main '
        'row-label main main main '
        'row-label main main main ';

    grid-template-columns: 72px 1fr 1fr 1fr;
    width: 100%;
`

const ColumnLabels = styled('div')`
    display: grid;
    grid-template-columns: 84px 1fr 1fr 1fr;
    grid-area: column-label;

    height: 34px;

    text-align: center;

    & > :not(:first-child) {
        background: ${tokens.ColorBackgroundDark};

        &:nth-child(2) {
            border-top-left-radius: ${tokens.SizingCornerS};
            border-bottom-left-radius: ${tokens.SizingCornerS};
        }
        &:last-child {
            border-top-right-radius: ${tokens.SizingCornerS};
            border-bottom-right-radius: ${tokens.SizingCornerS};
        }
        padding: ${tokens.SizingSpacing100};
    }
`

const RowLabels = styled('div')`
    display: grid;
    grid-template-rows: 20px 1fr 1fr 1fr;
    grid-area: row-label;

    text-align: center;
    width: 72px;
    padding: ${tokens.SizingSpacing100};
    align-items: center;
    border-radius: ${tokens.SizingCornerS};
    background: ${tokens.ColorBackgroundDark};
`

const MatrixItemsContainer = styled('div')`
    grid-area: main;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 1fr 1fr 1fr;

    grid-row-gap: 24px;
    width: 100%;

    & > div:not(:last-of-type):not(:nth-of-type(6)):not(:nth-of-type(3)) {
        border-right: 1px solid ${tokens.ColorBorderDefault};
    }
`
